import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { useAppSelector } from '../../../redux/store';
import { Modal } from '../../Modal/Modal';
import AddNewContactModal from '../../Modal/ModalActions/AddNewContactModal/AddNewContactModal';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmptyStateContainerProps {
  onModalOpenChange?: (open: boolean) => void;
  searchValue?: string;
}

const EmptyStateContainer = ({
  onModalOpenChange,
  searchValue = '',
}: EmptyStateContainerProps) => {
  const [showModal, setShowModal] = useState(false);
  const { settingsData } = useAppSelector((state) => state.settingsData);

  // Manual add is only allowed once autosave is enabled for this user
  const isAutosaveEnabled = !!settingsData?.autosave;
  const isValidEmail = EMAIL_REGEX.test(searchValue.trim());
  const isAddContactDisabled = !isAutosaveEnabled || !isValidEmail;

  useEffect(() => {
    onModalOpenChange?.(showModal);
  }, [showModal, onModalOpenChange]);

  return (
    <Container>
      <NoResult>No result found</NoResult>
      <ModalControl
        disabled={isAddContactDisabled}
        onClick={() => !isAddContactDisabled && setShowModal(true)}
      >
        <Bold>Add new contact</Bold>
        {searchValue && <SearchContent>{searchValue}</SearchContent>}
      </ModalControl>

      <Modal showModal={showModal} setShowModal={() => setShowModal(false)}>
        <AddNewContactModal email={searchValue} />
      </Modal>
    </Container>
  );
};

export default EmptyStateContainer;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  & > * {
    padding: 16px 10px;
    font-weight: 500;
    font-size: 14px;
    line-height: 30px;

    &:hover {
      background-color: #f9fafb;
    }
  }
`;

const ModalControl = styled.div<{ disabled?: boolean }>`
  display: grid;
  grid-template-columns: 115px minmax(auto, 240px);
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ theme, disabled }) =>
    disabled ? theme.palette.grayTextColor : theme.palette.blueColor};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  gap: 8px;
`;

const Bold = styled.b`
  font-weight: 700;
`;

const SearchContent = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 30px;
  display: flex;
  word-break: break-word;
`;

const NoResult = styled.div`
  cursor: not-allowed;
`;
